/**
 * AnswerFirst — the "TL;DR" block ChatGPT / Perplexity / Google AI
 * Overviews look for at the top of a page.
 *
 * Renders a compact, high-contrast paragraph immediately after the H1
 * so answer engines can cite the first 2–3 sentences directly. Also
 * emits a Speakable schema hint so voice assistants (Siri, Google
 * Assistant, Alexa) pick up the same sentence.
 *
 * Usage:
 *   <AnswerFirst>Central Rentals is Canadian-built property
 *   management software. From CA$35/mo.</AnswerFirst>
 */
import { useEffect } from "react";
import { Sparkles } from "lucide-react";

const SPEAKABLE_ID = "crc-speakable-jsonld";

export default function AnswerFirst({ children, label = "In one sentence" }) {
  useEffect(() => {
    // Idempotent: swap any existing Speakable schema for the fresh one.
    document.getElementById(SPEAKABLE_ID)?.remove();
    const script = document.createElement("script");
    script.id = SPEAKABLE_ID;
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["[data-answer-first]"],
      },
    });
    document.head.appendChild(script);
    return () => { document.getElementById(SPEAKABLE_ID)?.remove(); };
  }, []);

  return (
    <aside
      data-answer-first
      data-testid="answer-first-tldr"
      className="mt-5 mb-6 p-4 sm:p-5 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-sky-50/60"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-800 mb-2">
        <Sparkles className="w-3 h-3" /> {label}
      </div>
      <p className="text-[15px] sm:text-base text-slate-800 leading-relaxed font-medium">
        {children}
      </p>
    </aside>
  );
}
